import {useState, useCallback, type MouseEventHandler} from 'react';
import {FormattedMessage} from 'react-intl';
import {useMutation, type ApolloCache, type Reference} from '@apollo/client';
import {UPDATE_BOOKMARK_FOLDER_MUTATION, BOOKMARK_FOLDER_FRAGMENT, type UpdateBookmarkFolderOutput} from 'graphql/admin/bookmarks';
import {useSnackbar} from 'hooks';
import {getErrorMessage} from 'utils';
import {useSelectedLinkContext} from 'contexts/SelectedLinkContext';
import {EditIconButton, type EditIconButtonProps} from 'components/buttons';
import EditBookmarkFolderDialog from './EditBookmarkFolderDialog';

const updateFoldersCache = (cache: ApolloCache<any>, {data} : {data: UpdateBookmarkFolderOutput}) => {
  const updatedFolder = data?.adminUpdateFolder?.folder;
  if (updatedFolder) cache.modify({
    fields: {
      adminFoldersByFolderType({folders: existingFoldersRefs = [], ...adminFoldersByFolderType}) {
        const updatedFolderCacheRef = cache.writeFragment({
          data: updatedFolder,
          fragment: BOOKMARK_FOLDER_FRAGMENT,
          fragmentName: 'BookmarkFolder'
        });
        return {
          ...adminFoldersByFolderType,
          folders: existingFoldersRefs.map((folderRef: Reference) => folderRef.__ref === updatedFolderCacheRef.__ref ? updatedFolderCacheRef : folderRef)
        }
      }
    }
  });
};

type EditBookmarkFolderIconButtonProps = EditIconButtonProps & {
  folderName: string;
  folderKey: string;
}

const EditBookmarkFolderIconButton = ({folderName, folderKey} : EditBookmarkFolderIconButtonProps) : JSX.Element => {
  const [open, setOpen] = useState(false);

  const {selectedLink: {channelKey: channel}} = useSelectedLinkContext();
  const {successSnackbar} = useSnackbar();

  const onCompleted = (data /* : UpdateBookmarkFolderOutput */) => {
    const updatedFolder = data?.adminUpdateFolder?.folder;
    if (updatedFolder) {
      successSnackbar(<FormattedMessage id="admin.bookmarks.folder.update.success" values={{ folderName: updatedFolder.name }} />);
      handleClose();
    }
  }

  const [updateFolder, {loading, data, error, reset}] = useMutation/* <UpdateBookmarkFolderOutput> */(UPDATE_BOOKMARK_FOLDER_MUTATION, { update: updateFoldersCache, onCompleted });

  const handleClick : MouseEventHandler<HTMLButtonElement> = () => setOpen(true);

  const handleClose = useCallback(() => {
    setOpen(false);
    reset();
  }, [reset]);

  const handleEditFolder = ({newFolderName}) => updateFolder({variables: {adminUpdateFolderInput: {channel, folderKey, newFolderName}}});

  return <>
    <EditIconButton
      className='bg-admin-blue-400'
      labelId='admin.bookmarks.button.updateFolder'
      onClick={handleClick}
    />
    <EditBookmarkFolderDialog
      errorReason={data?.adminUpdateFolder?.errorReason || getErrorMessage(error)}
      folderName={folderName}
      open={open}
      onClose={handleClose}
      submitting={loading}
      handleEditFolder={handleEditFolder}
    />
  </>
}

export default EditBookmarkFolderIconButton;